import { useState } from "preact/hooks";
import { range, pairs } from "d3-array";

const generateBins = ({ min, max, step }) => {
  const binRange = pairs(
    range(Number(min), Number(max) + Number(step), Number(step))
  );
  const bins = binRange.map((d) => d.join(" - "));
  return bins;
};

const parseBinString = (binString, seperator = ",") =>
  binString.trim().split(seperator);

function NonUniformBins({ isUniformBins, onSetBins, onToggleUniformBins }) {
  const [binString, setBinString] = useState("");

  return (
    <div class="md:w-2/4">
      <div class="mx-2 bg-gray-300 rounded-lg shadow">
        <button
          onClick={onToggleUniformBins}
          // todo change to onToggleUniform
          class="card-btn"
          title="Use non-uniform bins"
          data-state={!isUniformBins ? "enabled" : "disabled"}
        >
          <h4 class="h4 text-white">Non-uniform</h4>
        </button>{" "}
        <div class="px-6 py-4">
          <div
            data-state={isUniformBins ? "disabled" : "enabled"}
            title={
              isUniformBins
                ? "Uniform bins is currently selected, click the blue heading with 'Non-uniform' to define non-uniform bins instead"
                : ""
            }
          >
            <p class="my-4">
              Seperate each bin header with a comma. For example{" "}
              <code>0-2.00,2.00-3.00,3.00-3.50,3.50-3.75</code>. This implies
              you can only use a dot as a decimal seperator for values. Of
              course you can also use qualitative descriptions like{" "}
              <code>low,medium,high</code>
            </p>
            <label class="font-semibold" for="non-uniform">
              Non-uniform bins
            </label>
            <input
              id="non-uniform"
              disabled={isUniformBins}
              value={binString}
              onChange={(e) => setBinString(e.target.value)}
              class="input"
              placeholder="0-2.0,2.0-3.0,3.0-3.5,3.5-3.75"
              type="text"
            />
            <button
              onClick={() => onSetBins(parseBinString(binString))}
              disabled={isUniformBins}
              class="btn btn-blue mt-4"
            >
              Generate Bins
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UniformBins({ isUniformBins, onToggleUniformBins, onSetBins }) {
  const [binProps, setBinProps] = useState({});

  return (
    <div class="md:w-2/4">
      <div class="mx-2 bg-gray-300 rounded-lg shadow">
        <button
          onClick={onToggleUniformBins}
          class="card-btn"
          title="Use uniform bins"
          data-state={isUniformBins ? "enabled" : "disabled"}
        >
          <h4 class="h4 text-white">Uniform</h4>
        </button>
        <div class="py-4 px-6 ">
          <div
            data-state={isUniformBins ? "enabled" : "disabled"}
            title={
              !isUniformBins
                ? "Non-uniform bins is currently selected, click the blue heading with 'Uniform' to define uniform bins"
                : ""
            }
          >
            <p class="my-4">
              Generate uniform bins based on a minimum and maximum value with a
              fixed bin size. Both start and end are <em>inclusive</em>, so the
              range will go up to and including the maximum value.
            </p>
            <div class="binprop">
              <label class="font-semibold" for="bin-min">
                Minimum
              </label>
              <input
                disabled={!isUniformBins}
                onChange={(e) =>
                  setBinProps({ ...binProps, min: e.target.value })
                }
                value={binProps.min}
                class="input"
                id="bin-min"
                name="Bin minumum"
                placeholder="0"
                inputMode="decimal"
                step="any"
                type="number"
              />
            </div>
            <div class="binprop">
              <label class="font-semibold" for="bin-max">
                Maximum
              </label>
              <input
                disabled={!isUniformBins}
                onChange={(e) =>
                  setBinProps({ ...binProps, max: e.target.value })
                }
                value={binProps.max}
                class="input"
                id="bin-max"
                name="Bin maximum"
                placeholder="6"
                inputMode="decimal"
                step="any"
                type="number"
              />
            </div>
            <div class="binprop">
              <label class="font-semibold" for="bin-size">
                Bin size
              </label>
              <input
                disabled={!isUniformBins}
                onChange={(e) =>
                  setBinProps({ ...binProps, step: e.target.value })
                }
                value={binProps.step}
                class="input"
                id="bin-size"
                name="Bin size"
                placeholder="1"
                inputMode="decimal"
                type="number"
                step="any"
              />
            </div>
            <button
              onClick={() => onSetBins(generateBins(binProps))}
              class="btn btn-blue mt-4"
              disabled={!isUniformBins}
            >
              Generate Bins
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DefineBins({
  isUniformBins,
  bins,
  onToggleUniformBins,
  onGoBackDirection,
  onConfirmBins,
  onSetBins,
}) {
  return (
    <div class="define-data-card">
      <h3 class="h3">Bins</h3>
      <p class="mb-2">The maximum number of bins is 9! Setting more than 9 bins is not accepted.</p>
      <div class="flex mb-4 flex-row flex-wrap flex-auto content-around ">
        <UniformBins
          isUniformBins={isUniformBins}
          onSetBins={onSetBins}
          onToggleUniformBins={onToggleUniformBins}
        />
        <NonUniformBins
          isUniformBins={isUniformBins}
          onSetBins={onSetBins}
          onToggleUniformBins={onToggleUniformBins}
        />
      </div>

      <p class="bg-gray-300 px-8 py-2 my-4 rounded flex items-center shadow">
        <span class="font-bold">Current bins:</span>
        {bins.length ? (
          bins.join(", ")
        ) : (
          <span class="text-gray-600">No bins generated yet</span>
        )}
      </p>

      <div class="flex justify-end pt-2">
        <button class="btn btn-blue ml-4" onClick={onGoBackDirection}>
          Go back
        </button>
        <button class="btn btn-blue ml-4" onClick={onConfirmBins}>
          Confirm bins
        </button>
      </div>
    </div>
  );
}

export { DefineBins };
